import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import type { UseFieldArrayRemove } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { MenuItemState } from '~/components/KebabMenu/components/KebabMenuItem';
import { ActionsMenu } from '~/modules/settings/components/ActionsMenu';
import { TruncateWithTooltip } from '~/modules/settings/components/TruncateWithTooltip';
import { DISTRIBUTION_TERRITORIES } from '~settings/api/lib/types';
import { LabelName } from '~settings/modules/label/components/LabelName';

import { DistributionRegions } from '../components/DistributionRegions/DistributionRegions';
import type { DistributionGroup } from '../components/LabelForm/schema';

export const useDistributionColumns = (
  onRemove: UseFieldArrayRemove,
  onEdit: (index: number) => void,
) => {
  const { t } = useTranslation('settings');

  return useMemo(() => {
    const columnHelper = createColumnHelper<DistributionGroup>();

    const distributionColumns = [
      columnHelper.accessor('name', {
        id: 'name',
        header: () => t('label.groupName'),
        cell: (cell) => (
          <div className="pl-md">
            <LabelName name={cell.getValue()} />
          </div>
        ),
        enableSorting: true,
      }),
      columnHelper.display({
        id: 'territories',
        header: () => t('territories'),
        cell: ({ row }) => {
          const countries =
            row.original.type !== DISTRIBUTION_TERRITORIES.Worldwide
              ? row.original?.distributionGroupCountries
              : [];
          const region = row.original.type;

          return <DistributionRegions countries={countries} region={region} />;
        },
        enableSorting: false,
      }),
      columnHelper.accessor('notes', {
        id: 'notes',
        header: () => t('notes'),
        cell: (cell) => {
          const notes = cell.getValue();

          return notes ? (
            <TruncateWithTooltip className="inline-block">
              {notes}
            </TruncateWithTooltip>
          ) : (
            '-'
          );
        },
        enableSorting: false,
        size: 120,
      }),
      columnHelper.display({
        id: 'actions',
        size: 30,
        cell: ({ row }) => {
          const actions = [
            {
              id: 'edit',
              label: t('buttons.edit'),
              callback: () => onEdit(row.index),
            },
            {
              id: 'delete',
              label: t('buttons.delete'),
              callback: () => onRemove(row.index),
              state: 'error' as MenuItemState,
            },
          ];

          return <ActionsMenu actions={actions} />;
        },
      }),
    ];

    return distributionColumns;
  }, [t, onRemove, onEdit]);
};
